import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, Typography, Button } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import { Shuffle, Gauge, SplitSquareHorizontal, LineChart, Plus } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

const { Title, Paragraph } = Typography;

interface CardData {
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  gradientFrom: string;
  gradientTo: string;
  iconGradient: string;
  badge?: string;
}

const RctExperimentLanding: React.FC = () => {
  const location = useLocation();
  const [animatedCards, setAnimatedCards] = useState<boolean[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards(new Array(4).fill(true));
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const cardData: CardData[] = [
    {
      title: "Randomized Market Pairing",
      icon: Shuffle,
      description:
        "Upload your DMA-level weekly data and let the design randomly split markets into Treatment and Control groups for a clean, unbiased test.",
      gradientFrom: "#3b82f6",
      gradientTo: "#8b5cf6",
      iconGradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
    },
    {
      title: "Split Scenario Comparison",
      icon: SplitSquareHorizontal,
      description:
        "Compare 50/50, 80/20, 70/30, and 60/40 test-control splits side by side on market counts, match quality, and weekly cost before committing to a design.",
      gradientFrom: "#10b981",
      gradientTo: "#06b6d4",
      iconGradient: "linear-gradient(135deg, #10b981, #06b6d4)",
    },
    {
      title: "Power & Investment Sizing",
      icon: Gauge,
      description:
        "Set candidate durations and splits, then size the weekly and total investment needed to hit your minimum detectable effect within budget.",
      gradientFrom: "#f59e0b",
      gradientTo: "#ef4444",
      iconGradient: "linear-gradient(135deg, #f59e0b, #ef4444)",
      badge: "New",
    },
    {
      title: "Trial-Level Validation",
      icon: LineChart,
      description:
        "Review the winning trial's actual vs. predicted performance, per-DMA Treatment/Control investment breakdown, and the full investment calculation.",
      gradientFrom: "#8b5cf6",
      gradientTo: "#ec4899",
      iconGradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
    },
  ];

  return (
    <div className="min-h-screen bg-white py-12 px-6 md:px-20">
      <div className="text-center mb-12">
        <Title level={1}>Design RCT Experiments</Title>
      </div>
      <div className="flex flex-col md:flex-row gap-6 justify-center items-center mb-12">
        <div className="relative group">
          <Link to="/rct-experiment/list">
            <Button icon={<UnorderedListOutlined />} size="large">
              View Past Tests
            </Button>
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to={`/rct-experiment/${uuidv4()}`}>
            <Button
              type="primary"
              icon={<Plus className="h-4 w-4" />}
              size="large"
              className="bg-gradient-to-r from-blue-600 to-purple-600 border-none hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Run New RCT Experiment
            </Button>
          </Link>
        </div>
      </div>

      {/* Feature Cards Section */}
      <div className="mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
          {cardData.map((card, index) => {
            const IconComponent = card.icon;
            const isAnimated = animatedCards[index];

            return (
              <Card
                key={index}
                className={`
                  shadow-lg hover:shadow-2xl rounded-2xl border border-gray-200
                  relative group overflow-hidden transition-all duration-700
                  transform hover:scale-105 hover:-translate-y-2
                  ${isAnimated ? "animate-in slide-in-from-bottom-4 fade-in" : "opacity-0"}
                `}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animationDuration: "800ms",
                  animationFillMode: "forwards",
                }}
                bodyStyle={{ padding: "24px" }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${card.gradientFrom}08, ${card.gradientTo}08)`,
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 20% 80%, ${card.gradientFrom}15 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${card.gradientTo}15 0%, transparent 50%)`,
                    }}
                  />
                </div>

                <div
                  className="absolute top-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out"
                  style={{ background: card.iconGradient }}
                />
                <div className="flex items-center gap-4 mb-4 relative z-10">
                  <div
                    className="p-3 rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-md"
                    style={{
                      background: card.iconGradient,
                      boxShadow: `0 4px 20px ${card.gradientFrom}40`,
                    }}
                  >
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>

                  <Title
                    level={4}
                    className="text-gray-800 font-semibold mb-0 group-hover:text-gray-900 transition-colors duration-300"
                  >
                    {card.title}
                  </Title>
                </div>

                <div className="relative z-10">
                  <Paragraph className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {card.description}
                  </Paragraph>
                </div>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: card.gradientFrom }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RctExperimentLanding;
